import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  HostListener,
  Inject,
  inject,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { NavItem } from '../../models/nav-item.model';
import { NavigationService } from '../../services/navigation.service';
import { LanguageService } from '../../../shared/services/language.service';
import { resolveSupportedLang, SUPPORTED_LANGS, SupportedLang } from '../../../shared/config/localized-paths';

/**
 * Mobile slide-in navigation drawer.
 * Visible only on viewports narrower than LG (< 992px).
 * Managed by NavigationService.isMobileNavOpen$.
 */
@Component({
  selector: 'xbh-mobile-nav',
  templateUrl: './mobile-nav.component.html',
  styleUrls: ['./mobile-nav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobileNavComponent implements OnInit {
  readonly isOpen$: Observable<boolean>;

  activeDrillDownIndex = -1;
  isLangPanelOpen = false;

  private readonly destroyRef = inject(DestroyRef);
  private readonly isBrowser: boolean;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly navigationService: NavigationService,
    private readonly languageService: LanguageService,
    private readonly translate: TranslateService,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) platformId: object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.isOpen$ = this.navigationService.isMobileNavOpen$;
  }

  ngOnInit(): void {
    // Close drawer on route change to prevent stale open state
    this.isOpen$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(isOpen => {
        if (!this.isBrowser) return;
        if (isOpen) {
          this.document.body.classList.add('myce-mobile-nav-open');
        } else {
          this.document.body.classList.remove('myce-mobile-nav-open');
          this.activeDrillDownIndex = -1;
          this.isLangPanelOpen = false;
        }
      });

    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.cdr.markForCheck());
  }

  get navItems(): NavItem[] {
    return this.navigationService.primaryNavItems;
  }

  get loginItems(): NavItem[] {
    return this.navigationService.loginItems;
  }

  get loginItem(): NavItem | null {
    return this.loginItems.find(item => item.id === 'mce-digital-login')
      ?? this.loginItems.find(item => !!item.href)
      ?? null;
  }

  get currentLang(): string {
    return this.translate.currentLang ?? 'en';
  }

  close(): void {
    this.navigationService.closeMobileNav();
  }

  drillDown(index: number): void {
    this.activeDrillDownIndex = index;
    this.cdr.markForCheck();
    setTimeout(() => this.languageService.applyLanguageVisibility(resolveSupportedLang(this.currentLang)));
  }

  drillUp(): void {
    this.activeDrillDownIndex = -1;
    this.isLangPanelOpen = false;
    this.cdr.markForCheck();
  }

  openLangPanel(): void {
    this.isLangPanelOpen = true;
    this.cdr.markForCheck();
  }

  closeLangPanel(): void {
    this.isLangPanelOpen = false;
    this.cdr.markForCheck();
  }

  get activeDrillDownItem(): NavItem | null {
    return this.activeDrillDownIndex >= 0
      ? (this.navItems[this.activeDrillDownIndex] ?? null)
      : null;
  }

  onLanguageClick(key: string): void {
    if (SUPPORTED_LANGS.includes(key as SupportedLang)) {
      this.languageService.setLanguage(key);
    }
    this.isLangPanelOpen = false;
    this.close();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.close();
  }

  onScrimClick(): void {
    this.close();
  }

  trackByLabel(_index: number, item: NavItem): string {
    return item.id ?? item.routeKey ?? item.href ?? item.labelEn;
  }
}
